import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Loader2, TrendingUp } from 'lucide-react';
import { API } from '../../../lib/api';
import { field, ghost, rs, useApi } from '../purchasing/ui';

const mLabel = (mk) => new Date(`${mk}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'short', year: '2-digit' });
const TONE = { 'Customer credit': 'bg-emerald-100 text-emerald-800', 'Customer debit': 'bg-amber-100 text-amber-800', 'Supplier credit': 'bg-sky-100 text-sky-800', 'Purchase debit': 'bg-violet-100 text-violet-800' };

const Heat = ({ n, max }) => (
  <td className="p-1 text-center">
    <span className="inline-block min-w-[28px] rounded px-1.5 py-1 text-xs font-mono" style={{ backgroundColor: n ? `rgba(15,118,110,${0.12 + 0.7 * (n / max)})` : 'transparent', color: n / max > 0.55 ? '#fff' : '#0f172a' }}>{n || '·'}</span>
  </td>
);

const TrendTable = ({ rows, months, kind, testid }) => {
  const max = Math.max(1, ...rows.flatMap((r) => months.map((m) => r.counts[m])));
  return (
    <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto">
      <table className="w-full text-sm" data-testid={testid}>
        <thead className="bg-slate-50 text-xs uppercase text-slate-500"><tr>
          <th className="p-3 text-left">{kind === 'reason' ? 'Reason' : 'Staff'}</th>
          {months.map((m) => <th key={m} className="p-1 font-medium">{mLabel(m)}</th>)}
          <th className="p-3 text-right">Notes</th><th className="p-3 text-right">Amount</th>
        </tr></thead>
        <tbody className="divide-y divide-slate-100">{rows.map((r, i) => (
          <tr key={i} className={r.rising ? 'bg-red-50/60' : ''} data-testid={`${testid}-row-${i}`}>
            <td className="p-3 min-w-[220px]">
              {kind === 'reason' ? <><span className={`text-[10px] font-semibold uppercase rounded-full px-2 py-0.5 mr-2 ${TONE[r.side]}`}>{r.side}</span>{r.reason}</> : r.name}
              {r.rising && <span className="ml-2 inline-flex items-center gap-0.5 text-[11px] font-semibold text-red-700" data-testid={`${testid}-rising-${i}`}><TrendingUp size={12} /> {r.last_month} → {r.this_month}</span>}
            </td>
            {months.map((m) => <Heat key={m} n={r.counts[m]} max={max} />)}
            <td className="p-3 text-right font-mono">{r.count}</td><td className="p-3 text-right font-mono">{rs(r.amount)}</td>
          </tr>))}
          {rows.length === 0 && <tr><td colSpan={months.length + 3} className="p-6 text-center text-slate-400">No notes in this period.</td></tr>}
        </tbody>
      </table>
    </div>
  );
};

// Notes per month by reason and by staff; reasons up on last month are flagged.
export default function NoteTrends() {
  const { call } = useApi();
  const [f, setF] = useState({ months: '12', party: '', kind: '' });
  const [d, setD] = useState(null);
  const qs = new URLSearchParams(Object.entries(f).filter(([, v]) => v)).toString();
  const load = useCallback(() => { setD(null); call('get', `/notes/trends?${qs}`).then(setD).catch(() => setD({ error: true })); }, [call, qs]);
  useEffect(() => { load(); }, [load]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const sel = (k, label, opts) => (
    <label className="text-xs text-slate-500">{label}<select className={`${field} mt-1`} value={f[k]} onChange={set(k)} data-testid={`trends-filter-${k}`}>{opts.map(([v, t]) => <option key={v} value={v}>{t}</option>)}</select></label>
  );
  return (
    <div className="p-4 sm:p-6 space-y-4" data-testid="note-trends-page">
      <div className="flex flex-wrap items-end gap-3">
        <div>
          <Link to="/admin/gst/notes" className="text-xs text-slate-500 inline-flex items-center gap-1 hover:text-slate-900" data-testid="trends-back"><ArrowLeft size={12} /> All notes</Link>
          <h1 className="text-xl font-semibold text-slate-900">Note trends</h1>
          <p className="text-sm text-slate-500">Issued notes each month, by reason and by who raised them. Reasons up on last month are shaded red.</p>
        </div>
        <div className="ml-auto flex gap-2">
          <a href={`${API}/notes/trends/export?${qs}&fmt=pdf`} target="_blank" rel="noopener noreferrer" className={ghost} data-testid="trends-pdf"><FileText size={14} /> PDF</a>
          <a href={`${API}/notes/trends/export?${qs}&fmt=xlsx`} className={ghost} data-testid="trends-excel"><Download size={14} /> Excel</a>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 items-end bg-white border border-slate-200 rounded-xl p-3">
        {sel('months', 'Period', [['6', 'Last 6 months'], ['12', 'Last 12 months'], ['24', 'Last 24 months']])}
        {sel('party', 'Side', [['', 'All'], ['customer', 'Customer'], ['supplier', 'Supplier']])}
        {sel('kind', 'Type', [['', 'All'], ['credit', 'Credit'], ['debit', 'Debit']])}
      </div>
      {!d ? <Loader2 className="animate-spin text-slate-400" /> : d.error ? <p className="text-sm text-red-600" data-testid="trends-error">Could not load the trends.</p> : <>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[['Notes in period', d.grand.count, 'trends-total-count'], ['Value of notes', rs(d.grand.amount), 'trends-total-amount'], ['Reasons rising this month', d.rising_count, 'trends-rising-count']].map(([k, v, t]) => (
            <div key={k} className={`rounded-xl border p-4 ${t === 'trends-rising-count' && d.rising_count ? 'border-red-200 bg-red-50' : 'border-slate-200 bg-white'}`}>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">{k}</div><div className="text-2xl font-semibold font-mono" data-testid={t}>{v}</div>
            </div>))}
        </div>
        <h2 className="text-sm font-semibold text-slate-900 pt-2">By reason</h2>
        <TrendTable rows={d.by_reason} months={d.months} kind="reason" testid="trends-reason-table" />
        <h2 className="text-sm font-semibold text-slate-900 pt-2">By staff</h2>
        <TrendTable rows={d.by_staff} months={d.months} kind="staff" testid="trends-staff-table" />
      </>}
    </div>
  );
}
